import React, {
  useContext, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Collapse from '@prism/collapse';
import { v4 as uuid } from 'uuid';
import { SelectableContext } from './context/SelectableContext';
import AccordionContext from './AccordionCtx';

const AccordionItem = ({
  children,
  triggerClassName,
  title,
  variant,
  defaultOpen,
  onClear,
  clearable,
  id,
  ...props
}) => {
  const [
    { openItems, open },
    { toggleItem, openItem, registerItem }] = useContext(AccordionContext);
  const [itemId, setItemId] = useState(id);
  const { toggleSelected } = useContext(SelectableContext) || {};
  const isFilter = variant === 'filter';

  useEffect(() => {
    const initialId = itemId || uuid();

    if (!itemId) {
      setItemId(initialId);
    }

    if (openItems && (open || defaultOpen)) {
      openItem(initialId);
    }

    if (registerItem) {
      registerItem(initialId);
    }
  }, []);

  const handleClick = () => {
    toggleItem(itemId);
  };

  const handleClear = (e) => {
    onClear(e);
    toggleSelected(false);
  };

  const isOpen = () => {
    if (!openItems) {
      return open || defaultOpen;
    }

    return openItems.includes(itemId);
  };

  const clsx = {
    trigger: classNames(
      triggerClassName,
      'accordion-trigger',
      isOpen() && 'accordion-trigger--active',
      isFilter && 'accordion-trigger--filter'
    ),
    caratDefault: classNames(
      'accordion-trigger__carat',
      isOpen() && 'accordion-trigger__carat--active',
      isFilter && 'accordion-trigger__carat--filter'
    ),
    appendIcon: classNames(
      isFilter && clearable && 'icon prism-icon-cancel-circle accordion-filter__cancel-icon',
      !isFilter && 'icon prism-icon-chevron-down accordion-carat',
    ),
    caratFilter: classNames(
      clearable && 'accordion-trigger__carat accordion-filter__cancel'
    ),
    wrap: classNames(
      'accordion-item__wrapper',
      isFilter && 'accordion-item__wrapper--filter'
    ),
  };

  return (
    <div
      {...props}
      id={itemId}
      className={clsx.wrap}
    >
      <span
        role="presentation"
        aria-label="Accordion trigger"
        className={clsx.trigger}
        onClick={handleClick}
      >
        {isFilter && (
          <span className={clsx.caratDefault}>
            <i className="icon prism-icon-chevron-right-light accordion-carat" />
          </span>
        )}
        <span className="accordion-trigger-label">{title}</span>
        {!!clsx.appendIcon && !isFilter && (
          <span
            role="presentation"
            aria-label="Accordion Clear"
            onClick={handleClear}
            className={isFilter ? clsx.caratFilter : clsx.caratDefault}
          >
            <i className={clsx.appendIcon} />
          </span>
        )}
      </span>
      <Collapse isOpen={isOpen()}>{children}</Collapse>
    </div>
  );
};

const propTypes = {
  id: PropTypes.string,
  triggerClassName: PropTypes.string,
  children: PropTypes.node,
  defaultOpen: PropTypes.bool,
  title: PropTypes.string,
  variant: PropTypes.string,
  onClear: PropTypes.func,
  clearable: PropTypes.bool,
};

AccordionItem.propTypes = propTypes;
AccordionItem.defaultProps = {
  id: undefined,
  children: null,
  defaultOpen: false,
  title: undefined,
  variant: undefined,
  onClear: () => {},
  triggerClassName: undefined,
  clearable: false,
};

export default AccordionItem;
